import React, { useEffect, useState } from "react";
import { ExternalLink, X, Lock, Copy, Check } from "lucide-react";
import { appPrompt } from "@/components/AppDialogHost";

/**
 * Detects when BroadcastFlow is being rendered inside another window
 * (Emergent's preview iframe) and offers a one-click escape into a
 * top-level browser tab. Chrome blocks file downloads from sandboxed
 * iframes; opening the app in its own tab is the only way to restore
 * full download functionality.
 *
 * Once dismissed, the user can re-trigger the banner by reloading.
 */
const DISMISS_KEY = "bfa_iframe_banner_dismissed_v1";

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin SecurityError when reading window.top → we're embedded.
    return true;
  }
}

export default function IframeEscapeBanner() {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isInIframe()) return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    setShow(true);
  }, []);

  if (!show) return null;

  const appUrl = window.location.href;

  const openInTab = () => {
    // 1) Try a normal popup (works if Emergent's iframe sandbox includes
    //    `allow-popups-to-escape-sandbox`).
    const w = window.open(appUrl, "_blank", "noopener,noreferrer");
    if (w) return;
    // 2) Popup blocked. Last resort: navigate the top frame. This *does*
    //    work cross-origin for write-only navigations.
    try {
      window.top.location.href = appUrl;
    } catch {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Final-final fallback: select the URL in a prompt() so user can
      // copy it manually. Some sandboxes block clipboard API too.
      await appPrompt("Copy this URL and open it in a new browser tab:", appUrl);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <div
      className="border-b border-amber-500/30 bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-transparent px-4 py-2.5 flex items-center gap-3 text-xs"
      data-testid="iframe-escape-banner"
    >
      <Lock className="h-4 w-4 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-amber-200 font-semibold">Downloads disabled.</span>
        <span className="text-ink-secondary ml-1.5">
          You&rsquo;re viewing inside a sandboxed preview. Open BroadcastFlow in its own tab to enable file downloads.
        </span>
      </div>
      <button
        onClick={openInTab}
        data-testid="iframe-escape-open"
        className="inline-flex items-center gap-1.5 rounded-md bg-amber-400 text-black px-3 py-1.5 text-xs font-bold hover:bg-amber-300 transition shrink-0"
      >
        <ExternalLink className="h-3.5 w-3.5" /> Open in standalone tab
      </button>
      <button
        onClick={copyLink}
        data-testid="iframe-escape-copy"
        className="inline-flex items-center gap-1.5 rounded-md border border-amber-400/40 text-amber-200 px-3 py-1.5 text-xs hover:bg-amber-500/10 transition shrink-0"
        title="Copy app URL — paste into a fresh browser tab"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button
        onClick={dismiss}
        data-testid="iframe-escape-dismiss"
        title="Dismiss for this session"
        className="rounded-md text-ink-muted hover:text-ink-primary p-1 shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
